import React, { useCallback, useMemo, useState, useRef, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { FaMicrophone, FaStop, FaTrash, FaSave, FaPaperclip } from "react-icons/fa";
import { toast } from "react-toastify";
import axios from "axios";
import { BACKEND } from "../../config/configEnvs";
import ModalInDrawer from "../ui/ModalInDrawer";
import StartSpeechRecognition from "../../utils/services/recordingAudioServices/StartSpeechRecognition";
import StopSpeechRecognition from "../../utils/services/recordingAudioServices/StopSpeechRecognition";

function SpeechModal({ activeModal, onClose, serviciosCotizaciones = [], cotizacion = null, pagos = [], igv = false }) {
  // Estados para grabación de audio
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [savedAudio, setSavedAudio] = useState(null);
  const [loadingSavedAudio, setLoadingSavedAudio] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Estados para transcripción de texto
  const [isDictating, setIsDictating] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState("");
  const [transcriptionText, setTranscriptionText] = useState("");
  const recognitionRef = useRef(null);
  const dictationBaseRef = useRef("");
  const dictatedFinalRef = useRef("");

  // Obtener datos de la cotización desde serviciosCotizaciones si no se pasan directamente
  const cotizacionData = useMemo(() => 
    cotizacion || serviciosCotizaciones?.[0]?.cotizacion || {}, 
    [cotizacion, serviciosCotizaciones]
  );
  const cliente = useMemo(() => cotizacionData?.cliente || {}, [cotizacionData]);
  const lead = useMemo(() => cotizacionData?.lead || {}, [cotizacionData]);
  const cotizador = useMemo(() => cotizacionData?.cotizador || {}, [cotizacionData]);

  // Función genérica para procesar templates y reemplazar placeholders
  const confirmTemplateProcess = useCallback((template) => {
    if (!template) {
      return "";
    }
    // Si es un array, tomar el primer elemento
    if (Array.isArray(template)) {
      template = template.length > 0 ? template[0] : null;
      if (!template) return "";
    }

    // Preparar datos dinámicos para reemplazo
    const now = new Date();
    const nombreServicio = serviciosCotizaciones?.[0]?.servicio?.name || "N/A";
    const numeroCotizacion = cotizacionData?.numeroCotizacion || "N/A";
    const nombreVendedor = cotizador?.name || "N/A";
    const nombreCliente = cliente?.nombreGerente || lead?.leadName || "N/A";
    const dniCliente = cliente?.dni || lead?.leadDNI || "N/A";
    const empresaCliente = cliente?.nombreComercial || lead?.leadCompany || cotizacionData?.empresa?.razonSocial || "Monstruo Creativo S.A.C";

    const fecha = format(now, "dd 'de' MMMM 'de' yyyy", { locale: es });
    const hora = format(now, "HH:mm", { locale: es });
    
    // Calcular monto total desde los pagos
    const montoTotal = (pagos || []).reduce((acc, p) => acc + (p.precio || 0) + ((p.montosServicios?.reduce((acc, ms) => acc + (ms.monto || 0), 0)) || 0), 0).toLocaleString("es-PE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const incluyeIGV = igv === true || igv === "si" ? "Incluye IGV" : "No incluye IGV";
    const cantidadCuotas = (pagos || []).length > 0 ? pagos.length : 1;
    const montoPorCuota = cantidadCuotas > 0 
      ? ((pagos || []).reduce((acc, p) => acc + (p.precio || 0) + ((p.montosServicios?.reduce((acc, ms) => acc + (ms.monto || 0), 0)) || 0), 0) / cantidadCuotas).toLocaleString("es-PE", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "0.00";

    // Función helper para reemplazar placeholders en un texto
    const replacePlaceholders = (text) => {
      if (!text || typeof text !== "string") return text;
      return text
        .replace(/{cotizacionId}/g, cotizacionData?.id || "N/A")
        .replace(/{nombreServicio}/g, nombreServicio)
        .replace(/{numeroCotizacion}/g, numeroCotizacion)
        .replace(/{nombreVendedor}/g, nombreVendedor)
        .replace(/{nombreCliente}/g, nombreCliente)
        .replace(/{dniCliente}/g, dniCliente)
        .replace(/{empresaCliente}/g, empresaCliente)
        .replace(/{fecha}/g, fecha)
        .replace(/{hora}/g, hora)
        .replace(/{montoTotal}/g, `S/. ${montoTotal}`)
        .replace(/{incluyeIGV}/g, incluyeIGV)
        .replace(/{cantidadCuotas}/g, cantidadCuotas.toString())
        .replace(/{montoPorCuota}/g, `S/. ${montoPorCuota}`);
    };

    // Caso 1: Es un objeto Delta de Quill (tiene propiedad ops)
    if (template && typeof template === "object" && template.ops && Array.isArray(template.ops) && template.ops.length >= 0) {
      // Clonar el Delta y reemplazar placeholders en los inserts manteniendo el formato
      const processedDelta = {
        ...template,
        ops: template.ops.map((op) => {
          // Si el insert es un string, reemplazar placeholders
          if (typeof op.insert === "string") {
            return {
              ...op,
              insert: replacePlaceholders(op.insert),
            };
          }
          // Si el insert es un objeto (por ejemplo, imágenes, embeds), mantenerlo tal cual
          return op;
        }),
      };
      return processedDelta;
    }

    // Caso 2: Es un string HTML
    if (typeof template === "string") {
      return replacePlaceholders(template);
    }

    // Caso 3: Es un objeto con propiedad html o content
    if (template && typeof template === "object") {
      if (template.html && typeof template.html === "string") {
        return replacePlaceholders(template.html);
      }
      if (template.content && typeof template.content === "string") {
        return replacePlaceholders(template.content);
      }
      // Si es un objeto pero no tiene estructura conocida, intentar stringify y reemplazar
      try {
        const jsonString = JSON.stringify(template);
        const processedJsonString = replacePlaceholders(jsonString);
        // Intentar parsear de vuelta a objeto
        return JSON.parse(processedJsonString);
      } catch (e) {
        console.warn("No se pudo procesar el objeto template:", template);
        return "";
      }
    }

    // Caso 4: Cualquier otro tipo, convertir a string y reemplazar
    return replacePlaceholders(String(template || ""));
  }, [serviciosCotizaciones, cotizacionData, cliente, lead, cotizador, pagos, igv]);

  // Función para procesar las confirmaciones y reemplazar placeholders
  const processConfirmaciones = useCallback((confirmacionesTemplate) => {
    if (!confirmacionesTemplate) return "";
    // Si es un array, tomar el primer elemento
    const template = Array.isArray(confirmacionesTemplate) ? confirmacionesTemplate[0] : confirmacionesTemplate;
    if (!template) return "";
    return confirmTemplateProcess(template);
  }, [confirmTemplateProcess]);

  // Calcular valores para la introducción
  const valoresIntroduccion = useMemo(() => {
    const now = new Date();
    // Añadir 5 horas para compensar el retraso por GMT -5
    const nowConAjuste = new Date(now.getTime() + 5 * 60 * 60 * 1000);
    
    // Obtener todos los nombres de servicios únicos
    const nombresServicios = serviciosCotizaciones
      ?.map((sc) => sc?.servicio?.name)
      .filter((nombre) => nombre && nombre.trim() !== "")
      .filter((nombre, index, self) => self.indexOf(nombre) === index) || [];
    
    // Formatear lista de servicios: "Servicio1, Servicio2 y Servicio3"
    const listaServicios = nombresServicios.length === 0 
      ? "N/A"
      : nombresServicios.length === 1
      ? nombresServicios[0]
      : nombresServicios.length === 2
      ? `${nombresServicios[0]} y ${nombresServicios[1]}`
      : `${nombresServicios.slice(0, -1).join(", ")}, y ${nombresServicios[nombresServicios.length - 1]}`;
    
    const nombreServicio = nombresServicios[0] || "N/A";
    const esPlural = nombresServicios.length > 1;
    const textoServicio = esPlural ? "de los servicios" : "del servicio";
    const textoServicioTotal = esPlural ? "de los servicios" : "del servicio";
    
    const cotizacionId = cotizacionData?.id || "N/A";
    const nombreVendedor = cotizador?.name || "N/A";
    const nombreCliente = cliente?.nombreGerente || lead?.leadName || "N/A";
    const dniCliente = cliente?.dni || lead?.leadDNI || "N/A";
    const empresaCliente = cliente?.nombreComercial || lead?.leadCompany || cotizacionData?.empresa?.razonSocial || "Monstruo Creativo S.A.C";
    const fecha = format(nowConAjuste, "dd 'de' MMMM 'de' yyyy", { locale: es });
    const hora = format(nowConAjuste, "HH:mm", { locale: es });
    
    // Calcular monto total considerando IGV por servicio
    // El precio de cada pago ya incluye el cálculo correcto con IGV
    const montoTotal = (pagos || []).reduce((acc, p) => {
      // Si el pago tiene montosServicios, calcular el total considerando IGV por servicio
      if (p.montosServicios && p.montosServicios.length > 0) {
        const totalPago = p.montosServicios.reduce((sum, ms) => {
          const montoBase = Number(ms.monto || 0);
          const montoIGV = ms.igv === "si" ? montoBase * 0.18 : 0;
          return sum + montoBase + montoIGV;
        }, 0);
        return acc + totalPago;
      }
      // Si no tiene montosServicios, usar el precio directamente
      return acc + (p.precio || 0);
    }, 0);
    
    const montoTotalFormateado = montoTotal.toLocaleString("es-PE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    
    const cantidadCuotas = (pagos || []).length > 0 ? pagos.length : 1;
    
    // Calcular el monto por cada cuota (cada pago) con fechas y desglose de servicios
    const montosPorCuota = (pagos || []).map((p, index) => {
      let totalPago = 0;
      const desgloseServicios = [];
      
      if (p.montosServicios && p.montosServicios.length > 0) {
        // Calcular total y desglose por servicio
        p.montosServicios.forEach((ms) => {
          const montoBase = Number(ms.monto || 0);
          const montoIGV = ms.igv === "si" ? montoBase * 0.18 : 0;
          const montoTotalServicio = montoBase + montoIGV;
          totalPago += montoTotalServicio;
          
          // Obtener el nombre del servicio
          let servicioId = null;
          if (ms.servicioId) {
            if (typeof ms.servicioId === "object") {
              servicioId = ms.servicioId?.id || ms.servicioId?.servicio?.id || null;
            } else {
              servicioId = ms.servicioId;
            }
          }
          
          let nombreServicio = `Servicio ${servicioId || "desconocido"}`;
          if (servicioId) {
            const servicioCotizacion = serviciosCotizaciones.find(
              (sc) => sc?.id === servicioId || sc?.servicio?.id === servicioId
            );
            if (servicioCotizacion?.servicio?.name) {
              nombreServicio = servicioCotizacion.servicio.name;
            }
          }
          
          desgloseServicios.push({
            nombre: nombreServicio,
            monto: montoTotalServicio.toLocaleString("es-PE", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
          });
        });
      } else {
        // Si no tiene montosServicios, usar el precio directamente
        totalPago = p.precio || 0;
      }
      
      // Obtener la fecha (priorizar fechaLimite, si no existe usar fechaPago)
      let fechaPago = null;
      if (p.fechaLimite) {
        fechaPago = typeof p.fechaLimite === "string" 
          ? p.fechaLimite 
          : p.fechaLimite.startDate || p.fechaLimite;
      } else if (p.fechaPago) {
        fechaPago = typeof p.fechaPago === "string"
          ? p.fechaPago
          : p.fechaPago.startDate || p.fechaPago;
      }
      
      // Formatear la fecha si existe
      let fechaFormateada = "";
      if (fechaPago) {
        try {
          const fechaObj = new Date(fechaPago);
          if (!isNaN(fechaObj.getTime())) {
            // Añadir 5 horas para compensar el retraso por GMT -5
            const fechaObjConAjuste = new Date(fechaObj.getTime() + 5 * 60 * 60 * 1000);
            fechaFormateada = format(fechaObjConAjuste, "dd 'de' MMMM 'de' yyyy", { locale: es });
          }
        } catch (e) {
          // Si hay error al formatear, usar la fecha original
          fechaFormateada = fechaPago;
        }
      }
      
      return {
        numero: index + 1,
        monto: totalPago.toLocaleString("es-PE", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        fecha: fechaFormateada,
        desgloseServicios: desgloseServicios,
      };
    });
    
    // Obtener todos los servicios únicos de todos los pagos y agruparlos por IGV
    const serviciosConIGV = [];
    const serviciosSinIGV = [];
    const serviciosMap = new Map(); // Para evitar duplicados
    
    (pagos || []).forEach((pago) => {
      if (pago.montosServicios && Array.isArray(pago.montosServicios)) {
        pago.montosServicios.forEach((ms) => {
          // Extraer el ID del servicio de manera robusta
          let servicioId = null;
          if (ms.servicioId) {
            if (typeof ms.servicioId === "object") {
              servicioId = ms.servicioId?.id || ms.servicioId?.servicio?.id || null;
            } else {
              servicioId = ms.servicioId;
            }
          }
          
          if (servicioId && !serviciosMap.has(servicioId)) {
            // Buscar el nombre del servicio en serviciosCotizaciones
            // El servicioId debería coincidir con el id de serviciosCotizaciones
            const servicioCotizacion = serviciosCotizaciones.find(
              (sc) => sc?.id === servicioId || sc?.servicio?.id === servicioId
            );
            const nombreServicio = servicioCotizacion?.servicio?.name || `Servicio ${servicioId}`;
            
            serviciosMap.set(servicioId, nombreServicio);
            
            if (ms.igv === "si") {
              serviciosConIGV.push(nombreServicio);
            } else {
              serviciosSinIGV.push(nombreServicio);
            }
          }
        });
      }
    });
    
    // Eliminar duplicados
    const serviciosConIGVUnicos = [...new Set(serviciosConIGV)];
    const serviciosSinIGVUnicos = [...new Set(serviciosSinIGV)];
    
    // Formatear texto de servicios con y sin IGV
    const formatearListaServicios = (lista) => {
      if (lista.length === 0) return "";
      if (lista.length === 1) return lista[0];
      if (lista.length === 2) return `${lista[0]} y ${lista[1]}`;
      return `${lista.slice(0, -1).join(", ")}, y ${lista[lista.length - 1]}`;
    };
    
    const textoServiciosConIGV = formatearListaServicios(serviciosConIGVUnicos);
    const textoServiciosSinIGV = formatearListaServicios(serviciosSinIGVUnicos);
    
    // Generar texto dinámico sobre IGV
    let textoIGV = "";
    if (serviciosConIGVUnicos.length > 0 && serviciosSinIGVUnicos.length > 0) {
      textoIGV = `Los servicios ${textoServiciosConIGV} incluyen IGV, mientras que ${textoServiciosSinIGV} no incluyen IGV.`;
    } else if (serviciosConIGVUnicos.length > 0) {
      textoIGV = `Todos los servicios (${textoServiciosConIGV}) incluyen IGV.`;
    } else if (serviciosSinIGVUnicos.length > 0) {
      textoIGV = `Los servicios (${textoServiciosSinIGV}) no incluyen IGV.`;
    }

    return {
      nombreServicio,
      listaServicios,
      esPlural,
      textoServicio,
      textoServicioTotal,
      cotizacionId,
      fecha,
      hora,
      nombreVendedor,
      nombreCliente,
      dniCliente,
      empresaCliente,
      montoTotal: `S/. ${montoTotalFormateado}`,
      cantidadCuotas: cantidadCuotas.toString(),
      montosPorCuota: montosPorCuota,
      textoIGV,
    };
  }, [serviciosCotizaciones, cotizacionData, cliente, lead, cotizador, pagos]);

  // Funciones para grabación de audio
  const startRecording = async () => {
    if (audioBlob) {
      toast.error('Ya tienes un audio grabado. Elimínalo primero para poder grabar uno nuevo.');
      return;
    }

    if (savedAudio) {
      toast.error('Ya hay un audio guardado en esta cotización. No se puede grabar un nuevo audio.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        const audioFile = new File([blob], `speech-${Date.now()}.wav`, { type: 'audio/wav' });
        const url = URL.createObjectURL(blob);
        setAudioBlob(audioFile);
        setAudioUrl(url);
        
        // Liberar el micrófono
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }
        audioChunksRef.current = [];
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }
      };

      recorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Timer para mostrar el tiempo de grabación
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      // Iniciar transcripción automáticamente cuando comienza la grabación
      if (!isDictating) {
        dictationBaseRef.current = (transcriptionText || "").trim();
        dictatedFinalRef.current = "";
        StartSpeechRecognition({
          setIsDictating,
          dictatedFinalRef,
          setInterimTranscript,
          dictationBaseRef,
          setNewComment: setTranscriptionText,
          isRecordingRef: { current: true },
          recognitionRef,
        });
      }

      toast.success('Grabación iniciada');
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error('Error al acceder al micrófono. Por favor, permite el acceso al micrófono.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      // Detener transcripción automáticamente cuando se detiene la grabación
      if (isDictating) {
        StopSpeechRecognition({
          recognitionRef,
          setIsDictating,
          setInterimTranscript,
          dictationBaseRef,
          dictatedFinalRef,
          setNewComment: setTranscriptionText,
        });
      }
      
      toast.success('Grabación finalizada');
    }
  };

  const clearAudio = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
  };

  const handleFileAttach = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset para permitir seleccionar el mismo archivo otra vez
    event.target.value = "";

    const allowedTypes = [
      "audio/mpeg", "audio/mp3", "audio/wav", "audio/wave", "audio/x-wav",
      "audio/ogg", "audio/webm", "audio/aac", "audio/mp4", "audio/x-m4a",
      "audio/flac",
    ];
    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(mp3|wav|ogg|webm|aac|m4a|flac|mp4)$/i)) {
      toast.error("Formato de audio no soportado. Usa MP3, WAV, OGG, WebM, AAC, M4A o FLAC.");
      return;
    }

    const maxSizeMB = 50;
    if (file.size > maxSizeMB * 1024 * 1024) {
      toast.error(`El archivo excede el tamaño máximo de ${maxSizeMB} MB.`);
      return;
    }

    const url = URL.createObjectURL(file);
    setAudioBlob(file);
    setAudioUrl(url);
    toast.success(`Archivo "${file.name}" adjuntado correctamente.`);
  };

  const saveAudio = async () => {
    if (!audioBlob) {
      toast.error('No hay audio para guardar. Por favor, graba un audio primero.');
      return;
    }

    if (savedAudio) {
      toast.error('Ya hay un audio guardado en esta cotización. No se puede guardar un nuevo audio.');
      return;
    }

    if (!cotizacionData?.id) {
      toast.error('No se pudo identificar la cotización. Por favor, intenta nuevamente.');
      return;
    }

    const confirm = window.confirm('¿Estás seguro de querer guardar el audio? Una vez guardado, no podrás editarlo ni eliminarlo.');
    if (!confirm) {
      return;
    }

    setIsSaving(true);
    try {
      // 1. Subir el audio a la colección SpeechCotizaciones
      const formData = new FormData();
      formData.append("file", audioBlob);
      
      // Opcional: agregar el ID de la cotización en el formData si el backend lo requiere
      formData.append("cotizacion", cotizacionData.id);

      const response = await axios.post(`${BACKEND}/api/speechCotizaciones`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // 2. Extraer el ID del speech
      const speechId = response?.data?.doc?.id || response?.data?.id;
      
      if (!speechId) {
        toast.error('No se pudo obtener el ID del audio guardado.');
        return;
      }

      // 3. Actualizar la cotización con el ID del speech como string y el texto transcrito
      await axios.put(`${BACKEND}/api/cotizaciones/${cotizacionData.id}`, {
        speech: speechId.toString(),
        speechText: transcriptionText || ""
      });

      // 4. Cargar el audio guardado para mostrarlo
      const savedAudioResponse = await axios.get(`${BACKEND}/api/speechCotizaciones/${speechId}`);
      // Manejar diferentes estructuras de respuesta
      const audioData = savedAudioResponse?.data?.doc || savedAudioResponse?.data || null;
      setSavedAudio(audioData);
      
      // 5. Recargar la cotización para obtener los datos actualizados
      try {
        const cotizacionResponse = await axios.get(`${BACKEND}/api/cotizaciones/${cotizacionData.id}`);
        const updatedCotizacion = cotizacionResponse?.data?.doc || cotizacionResponse?.data;
        // Actualizar el texto transcrito desde la cotización actualizada
        if (updatedCotizacion?.speechText) {
          setTranscriptionText(updatedCotizacion.speechText);
        }
      } catch (error) {
        console.error('Error al recargar la cotización:', error);
      }
      
      // 6. Limpiar el audio grabado
      clearAudio();

      toast.success('Audio guardado exitosamente en la cotización.');
    } catch (error) {
      console.error('Error al guardar el audio:', error);
      toast.error('Error al guardar el audio. Por favor, intenta nuevamente.');
    } finally {
      setIsSaving(false);
    }
  };

  // Cargar audio guardado cuando se abre el modal
  useEffect(() => {
    const loadSavedAudio = async () => {
      if (!activeModal || !cotizacionData?.id) {
        // Si el modal está cerrado, no hacer nada
        return;
      }
      
      // Recargar la cotización para obtener los datos más recientes
      let currentCotizacionData = cotizacionData;
      try {
        const cotizacionResponse = await axios.get(`${BACKEND}/api/cotizaciones/${cotizacionData.id}`);
        currentCotizacionData = cotizacionResponse?.data?.doc || cotizacionResponse?.data || cotizacionData;
      } catch (error) {
        console.error('Error al recargar la cotización:', error);
        // Si falla, usar los datos que ya tenemos
      }
      
      const speechData = currentCotizacionData?.speech;
      if (!speechData) {
        setSavedAudio(null);
        // Si no hay audio guardado, limpiar el texto transcrito también
        setTranscriptionText("");
        return;
      }

      // Si ya viene como objeto completo (populado por el backend)
      if (typeof speechData === 'object' && speechData.id) {
        setSavedAudio(speechData);
        // Cargar el texto transcrito desde la cotización si existe
        if (currentCotizacionData?.speechText) {
          setTranscriptionText(currentCotizacionData.speechText);
        } else {
          setTranscriptionText("");
        }
        return;
      }

      // Si viene como ID (string), obtener el documento
      const speechId = typeof speechData === 'string' ? speechData : speechData?.id;
      if (!speechId) {
        setSavedAudio(null);
        setTranscriptionText("");
        return;
      }

      setLoadingSavedAudio(true);
      try {
        const response = await axios.get(`${BACKEND}/api/speechCotizaciones/${speechId}`);
        // Manejar diferentes estructuras de respuesta
        const audioData = response?.data?.doc || response?.data || null;
        setSavedAudio(audioData);
        
        // Cargar el texto transcrito desde la cotización si existe
        if (currentCotizacionData?.speechText) {
          setTranscriptionText(currentCotizacionData.speechText);
        } else {
          setTranscriptionText("");
        }
      } catch (error) {
        console.error('Error al cargar el audio guardado:', error);
        setSavedAudio(null);
        setTranscriptionText("");
      } finally {
        setLoadingSavedAudio(false);
      }
    };

    loadSavedAudio();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModal, cotizacionData?.id]);

  // Limpiar cuando se cierre el modal (solo datos temporales, no los guardados)
  useEffect(() => {
    if (!activeModal) {
      // Detener grabación si está activa
      if (isRecording && mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
        setIsRecording(false);
      }
      // Detener transcripción si está activa
      if (isDictating) {
        onToggleDictation(false);
      }
      // Limpiar solo los datos temporales (audio grabado/adjuntado pero no guardado)
      const currentAudioUrl = audioUrl;
      if (currentAudioUrl) {
        URL.revokeObjectURL(currentAudioUrl);
      }
      setAudioBlob(null);
      setAudioUrl(null);
      setInterimTranscript("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      // NO limpiar savedAudio ni transcriptionText porque están guardados en la BD
      // Se recargarán cuando se abra el modal nuevamente
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModal]);

  // Limpiar al desmontar
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      // Limpiar transcripción si está activa
      const currentRecognition = recognitionRef.current;
      if (currentRecognition) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        StopSpeechRecognition({
          recognitionRef,
          setIsDictating,
          setInterimTranscript,
          dictationBaseRef,
          dictatedFinalRef,
          setNewComment: setTranscriptionText,
        });
      }
      const currentAudioUrl = audioUrl;
      if (currentAudioUrl) {
        URL.revokeObjectURL(currentAudioUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Formatear tiempo de grabación
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Función para toggle de dictación
  const onToggleDictation = useCallback((next) => {
    if (next) {
      dictationBaseRef.current = (transcriptionText || "").trim();
      dictatedFinalRef.current = "";
      StartSpeechRecognition({
        setIsDictating,
        dictatedFinalRef,
        setInterimTranscript,
        dictationBaseRef,
        setNewComment: setTranscriptionText,
        isRecordingRef: { current: true },
        recognitionRef,
      });
    } else {
      StopSpeechRecognition({
        recognitionRef,
        setIsDictating,
        setInterimTranscript,
        dictationBaseRef,
        dictatedFinalRef,
        setNewComment: setTranscriptionText,
      });
    }
  }, [transcriptionText]);

  return (
    <ModalInDrawer
      activeModal={activeModal}
      onClose={onClose}
      title="Speech de venta"
      className="max-w-6xl" 
      centered
      disableOutsideClick
    >
      <div className="p-4 max-h-[calc(80vh-120px)] overflow-y-auto pb-24 space-y-4">

        {/* Introducción */}
        <section className="space-y-3 ">
          <h2 className="text-xs font-semibold text-gray-800 border-b pb-2">Introducción</h2>
          <p className="text-xs text-gray-700 leading-relaxed">
            Esta llamada está siendo grabada para efectos de registro {valoresIntroduccion.textoServicio} <span className="font-semibold text-blue-600">{valoresIntroduccion.listaServicios}</span>, correspondiente{valoresIntroduccion.esPlural ? "s" : ""} a la cotización número <span className="font-semibold text-blue-600">{valoresIntroduccion.cotizacionId}</span>.
          </p>
          <p className="text-xs text-gray-700 leading-relaxed">
            Hoy <span className="font-semibold text-blue-600">{valoresIntroduccion.fecha}</span>, a las <span className="font-semibold text-blue-600">{valoresIntroduccion.hora}</span>, la contratación está siendo atendida por <span className="font-semibold text-blue-600">{valoresIntroduccion.nombreVendedor}</span>, y está siendo realizada por <span className="font-semibold text-blue-600">{valoresIntroduccion.nombreCliente}</span>, identificado(a) con DNI <span className="font-semibold text-blue-600">{valoresIntroduccion.dniCliente}</span>, representante de <span className="font-semibold text-blue-600">{valoresIntroduccion.empresaCliente}</span>.
          </p>
          <p className="text-xs text-gray-700 leading-relaxed">
            El costo total {valoresIntroduccion.textoServicioTotal} es de <span className="font-semibold text-blue-600">{valoresIntroduccion.montoTotal}</span>, y se cancelará en <span className="font-semibold text-blue-600">{valoresIntroduccion.cantidadCuotas}</span> cuota(s):
          </p>
          <ul className="text-xs text-gray-700 leading-relaxed ml-4 space-y-1 list-none">
            {valoresIntroduccion.montosPorCuota.map((pago, index) => (
              <li key={index} className="flex items-start">
                <span className="font-semibold text-blue-600 mr-2">*</span>
                <span className="font-semibold text-blue-600">
                  S/. {pago.monto}
                  {pago.fecha && ` - ${pago.fecha}`}
                  {pago.desgloseServicios && pago.desgloseServicios.length > 0 && (
                    <span className="text-black font-normal">
                      {" ("}
                      {pago.desgloseServicios.map((servicio, idx) => (
                        <span key={idx}>
                          <span className="font-semibold text-green-600">S/. {servicio.monto}</span>
                          {" para "}
                          <span className="font-semibold text-purple-600">{servicio.nombre}</span>
                          {idx < pago.desgloseServicios.length - 1 && ", "}
                        </span>
                      ))}
                      {")"}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
          {valoresIntroduccion.textoIGV && (
            <p className="text-xs text-gray-700 leading-relaxed">
              {valoresIntroduccion.textoIGV}
            </p>
          )}
        </section>

        {/* Información importante */}
        <section className="space-y-3 bg-blue-50 p-4 rounded-sm border border-blue-200">
          <h2 className="text-sm font-semibold text-gray-800">Información Importante</h2>
          <p className="text-xs text-gray-700 leading-relaxed">
            Es importante precisar que, una vez realizado el abono correspondiente, se procederá con el envío de sus credenciales al CRM, la activación del servicio y, de corresponder, el contrato formal para su revisión y firma.
          </p>
          <p className="text-xs text-gray-700 leading-relaxed">
            Confirmamos también que usted ha revisado la cotización enviada, comprendiendo correctamente el alcance, las características, los límites del servicio y los montos establecidos.
          </p>
        </section>

        {/* Confirmación de comprensión */}
        <section className="space-y-3 bg-purple-50 p-4 rounded-sm border border-purple-200">
          <h2 className="text-sm font-semibold text-purple-800 flex items-center gap-2">
            <span className="text-xl">✅</span>
            CONFIRMACIÓN DE COMPRENSIÓN
          </h2>
          <div className="bg-white p-4 rounded-md border-l-4 border-purple-500">
            <p className="text-xs text-gray-800 font-medium italic">
              "Si ha comprendido todo lo mencionado hasta este momento, por favor indíquelo diciendo: <span className="font-bold text-purple-700">'He entendido'</span>."
            </p>
          </div>
          <p className="text-xs text-gray-600 italic">
            (El asesor espera respuesta. Una vez el cliente dice "He entendido", continúa:)
          </p>
        </section>

        {/* Transición a los puntos del servicio */}
        <section className="space-y-3 bg-yellow-50 p-4 rounded-sm border border-yellow-200">
          <h2 className="text-sm font-semibold text-yellow-800 flex items-center gap-2">
            <span className="text-xl">📋</span>
            TRANSICIÓN A LOS PUNTOS DEL SERVICIO
          </h2>
          <div className="bg-white p-4 rounded-md border-l-4 border-yellow-500">
            <p className="text-xs text-gray-800 leading-relaxed">
              "Perfecto. A continuación, procedo a leerle los puntos pendientes para confirmar {valoresIntroduccion.textoServicioTotal} <span className="font-semibold text-blue-600">{valoresIntroduccion.listaServicios}</span>. Durante esta lectura, si identifica algún punto que no coincide con lo conversado previamente, le agradeceré indicarlo para realizar la aclaración correspondiente. La finalidad es confirmar que toda la información coincide exactamente con lo acordado anteriormente."
            </p>
          </div>
        </section>

        {/* Lectura de las confirmaciones del servicio */}
        <section className="space-y-3 bg-green-50 p-4 rounded-sm border border-green-200">
          <h2 className="text-sm font-semibold text-green-800 flex items-center gap-2">
            <span className="text-xl">📖</span>
            LECTURA DE LAS CONFIRMACIONES DEL SERVICIO
          </h2>
          <div className="bg-white p-4 rounded-md space-y-4">
            {(() => {
              // Filtrar servicios que tienen confirmaciones y needConfirmations es true
              const serviciosConConfirmaciones = serviciosCotizaciones?.filter((servicioCotizacion) => {
                // Verificar que el servicio tenga needConfirmations habilitado
                if (!servicioCotizacion?.servicio?.needConfirmations) {
                  return false;
                }
                const confirmaciones = servicioCotizacion?.confirmacionesServicio;
                return confirmaciones && (
                  (Array.isArray(confirmaciones) && confirmaciones.length > 0) ||
                  (typeof confirmaciones === "object" && confirmaciones.ops && Array.isArray(confirmaciones.ops))
                );
              }) || [];

              if (serviciosConConfirmaciones.length === 0) {
                return (
                  <p className="text-xs text-gray-600 italic text-center py-4">
                    No hay confirmaciones configuradas para los servicios de esta cotización.
                  </p>
                );
              }

              return serviciosConConfirmaciones.map((servicioCotizacion, index) => {
                // Obtener las confirmaciones del servicio
                const confirmaciones = servicioCotizacion?.confirmacionesServicio;
                const nombreServicio = servicioCotizacion?.servicio?.name || `Servicio ${index + 1}`;
                
                // Procesar confirmaciones con placeholders reemplazados
                const confirmacionesProcesadas = processConfirmaciones(confirmaciones);
                
                // Si no hay confirmaciones válidas, no mostrar nada
                if (!confirmacionesProcesadas) return null;

                // Valor seguro para ReactQuill
                let quillValue = null;
                if (typeof confirmacionesProcesadas === "object" && confirmacionesProcesadas.ops && Array.isArray(confirmacionesProcesadas.ops)) {
                  quillValue = confirmacionesProcesadas;
                } else if (typeof confirmacionesProcesadas === "string") {
                  // Si es un string, convertirlo a formato Delta
                  quillValue = { ops: [{ insert: confirmacionesProcesadas }] };
                } else {
                  quillValue = { ops: [{ insert: String(confirmacionesProcesadas || "") }] };
                }

                return (
                  <div key={servicioCotizacion?.id || index} className="border-b border-gray-200 pb-4 last:border-b-0 last:pb-0">
                    <h3 className="text-xs font-semibold text-gray-800 mb-2">
                      {nombreServicio}
                    </h3>
                    <div className="bg-gray-50 p-3 rounded-md">
                      <ReactQuill
                        theme="snow"
                        value={quillValue}
                        readOnly={true}
                        modules={{
                          toolbar: false,
                        }}
                        formats={[
                          'header',
                          'bold', 'italic', 'underline', 'strike',
                          'list', 'bullet',
                          'color', 'background',
                          'link'
                        ]}
                      />
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </section>

        {/* Solicitud de aceptación */}
        <section className="space-y-3 bg-orange-50 p-4 rounded-sm border border-orange-200">
          <h2 className="text-sm font-semibold text-orange-800 flex items-center gap-2">
            <span className="text-xl">👍</span>
            SOLICITUD DE ACEPTACIÓN
          </h2>
          <div className="bg-white p-4 rounded-md border-l-4 border-orange-500">
            <p className="text-xs text-gray-800 font-medium italic">
              "Señor(a) <span className="font-semibold text-blue-600">{valoresIntroduccion.nombreCliente}</span>, si está conforme con lo leído y explicado, le agradeceré confirmarlo diciendo explícitamente: <span className="font-bold text-orange-700">'Sí, acepto'</span>."
            </p>
          </div>
        </section>

        {/* Cierre formal */}
        <section className="space-y-3 bg-indigo-50 p-4 rounded-sm border border-indigo-200">
          <h2 className="text-sm font-semibold text-indigo-800 flex items-center gap-2">
            <span className="text-xl">🎉</span>
            CIERRE FORMAL
          </h2>
          <div className="bg-white p-4 rounded-md border-l-4 border-indigo-500 space-y-2">
            <p className="text-xs text-gray-800 leading-relaxed">
              Perfecto, su aceptación queda debidamente registrada.
            </p>
            <p className="text-xs text-gray-800 leading-relaxed">
              La grabación de esta llamada estará disponible dentro de su CRM de Cliente.
            </p>
            <p className="text-xs text-gray-800 leading-relaxed">
              Con esto damos por concluido el proceso de contratación {valoresIntroduccion.textoServicioTotal} <span className="font-semibold text-blue-600">{valoresIntroduccion.listaServicios}</span>.
            </p>
            <p className="text-xs text-gray-800 leading-relaxed font-semibold">
              Gracias por confiar en nosotros.
            </p>
          </div>
        </section>

        {/* Sección de transcripción de texto */}
        <section className="space-y-3 bg-gray-50 p-4 rounded-sm border border-gray-200">
          <h2 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
            <span className="text-xl">📝</span>
            TRANSCRIPCIÓN DE LA LLAMADA
          </h2>
          <div className="bg-white p-4 rounded-md space-y-2">
            <div className="flex flex-row items-start gap-2">
              <div className="w-full flex flex-col flex-1">
                <textarea
                  className="w-full p-3 border text-xs border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Escribe notas o usa la transcripción de voz aquí..."
                  value={transcriptionText}
                  disabled={!!savedAudio}
                  onChange={(e) => setTranscriptionText(e.target.value)}
                  rows={4}
                />
              </div>
            </div>
            {isDictating && (
              <div className="text-[10px] text-gray-500 mt-2">
                Dictado: <span className="italic">{interimTranscript || "escuchando..."}</span>
              </div>
            )}
          </div>
        </section>
      </div>

      {/* Input oculto para adjuntar archivo de audio */}
      <input
        type="file"
        ref={fileInputRef}
        accept="audio/*,.mp3,.wav,.ogg,.webm,.aac,.m4a,.flac"
        className="hidden"
        onChange={handleFileAttach}
      />

      {/* Botón sticky de grabación en la parte inferior */}
      <div className="sticky bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-4 z-[9999] shadow-sm">
        <div className="flex flex-col gap-4">
          {/* Reproductor de audio guardado */}
          {savedAudio && (
            <div className="flex flex-row gap-2 p-3 bg-green-50 border border-green-200 rounded-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold whitespace-nowrap text-green-800">Audio guardado:</span>
              </div>
              <audio 
                controls 
                className="w-full"
                src={savedAudio?.url || savedAudio?.file?.url}
              >
                Tu navegador no soporta el elemento de audio.
              </audio>
            </div>
          )}

          {/* Reproductor de audio grabado (no guardado aún) */}
          {audioBlob && !isRecording && !savedAudio && audioUrl && (
            <div className="flex flex-col gap-2 p-3 bg-purple-50 border border-purple-200 rounded-sm">
              <div className="flex items-center gap-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold whitespace-nowrap text-purple-800">Audio grabado (sin guardar):</span>
              </div>
                <audio 
                  controls 
                  className="flex-1"
                  src={audioUrl}
                >
                  Tu navegador no soporta el elemento de audio.
                </audio>
                <div className="flex items-center gap-2">
                  <button
                    onClick={saveAudio}
                    disabled={isSaving}
                    className="flex w-full justify-center bg-blue-50 rounded-lg border border-blue-200 items-center gap-2 hover:bg-blue-200 transition-all duration-100 text-blue-700 px-4 py-2"
                    >
                    <span className="text-sm text-blue-700">{isSaving ? 'Guardando...' : 'Guardar'}</span>
                    <FaSave className="text-blue-700"/>
                  </button>
                  <button
                    onClick={clearAudio}
                    className="flex w-full justify-center bg-red-50 rounded-lg border border-red-200 items-center gap-2 hover:bg-red-200 transition-all duration-100 text-red-700 px-4 py-2"
                  >
                    <span className="text-sm text-red-700">Eliminar</span>
                    <FaTrash className="text-red-700"/>
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              {isRecording && (
                <div className="flex items-center gap-2 whitespace-nowrap">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                  <span className="text-xs font-medium text-gray-700">
                    Grabando: {formatTime(recordingTime)}
                  </span>
                </div>
              )}
              {loadingSavedAudio && (
                <span className="text-xs text-gray-600 whitespace-nowrap">Cargando audio guardado...</span>
              )}
              {savedAudio && (
                <span className="text-xs text-green-600 font-medium whitespace-nowrap">✓ Audio guardado en la cotización</span>
              )}
            </div>

            <div className="flex items-center gap-2">
              {!isRecording && !audioBlob && !savedAudio && (
                <>
                  <button
                    onClick={startRecording}
                    className="flex w-full justify-center bg-green-50 rounded-lg border border-green-200 items-center gap-2 hover:bg-green-200 transition-all duration-100 text-green-700 px-4 py-2"
                  >
                    <span className="text-sm text-green-700">Grabar</span>
                    <FaMicrophone className="text-green-700"/>
                  </button>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="flex w-full justify-center bg-indigo-50 rounded-lg border border-indigo-200 items-center gap-2 hover:bg-indigo-200 transition-all duration-100 text-indigo-700 px-4 py-2"
                  >
                    <span className="text-sm text-indigo-700">Adjuntar</span>
                    <FaPaperclip className="text-indigo-700"/>
                  </button>
                </>
              )}

              {isRecording && (
                <button
                  onClick={stopRecording}
                  className="flex w-full justify-center bg-red-50 rounded-lg border border-red-200 items-center gap-2 hover:bg-red-200 transition-all duration-100 text-red-700 px-4 py-2"
                >
                  <span className="text-sm text-red-700">Detener grabación</span>
                  <FaStop className="text-red-700"/>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </ModalInDrawer>
  );
}

export default SpeechModal;